import type { EvalReviewView } from "@tutor/protocol";
import { useCallback, useEffect, useId, useState } from "react";
import { useLocale } from "../i18n";
import * as api from "./api";
import { Row, Toggle } from "./controls";
import { errorKey } from "./errors";
import { useParentT, type ParentStringKey } from "./strings";

// Conversation review for the whole household (ADR-0050): on by default, one switch to turn
// it off. Either way needs a fresh email code, which onAuthError turns into a new sign-in.

export function EvalReview(props: { onAuthError: (error: api.ApiError) => boolean }) {
  const { onAuthError } = props;
  const t = useParentT();
  const locale = useLocale();
  const labelId = useId();
  const [view, setView] = useState<EvalReviewView | null>(null);
  const [error, setError] = useState<ParentStringKey>();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.getEvalReview();
    if (res.ok) setView(res.value);
    else if (!onAuthError(res.error)) setError(errorKey(res.error));
  }, [onAuthError]);

  useEffect(() => {
    void load();
  }, [load]);

  const change = async (allowed: boolean) => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    const res = await api.putEvalReview(allowed);
    setBusy(false);
    if (res.ok) setView(res.value);
    else if (!onAuthError(res.error)) setError(errorKey(res.error));
  };

  const objectedAt = view?.objectedAt ?? null;
  const since =
    objectedAt === null
      ? null
      : new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(Date.parse(objectedAt));

  return (
    <section className="pm-group" aria-labelledby={`${labelId}-title`}>
      <h3 id={`${labelId}-title`} className="pm-group__title">
        {t("review.title")}
      </h3>
      {error === undefined ? null : (
        <p className="parent-message" role="alert">
          {t(error)}
        </p>
      )}
      {view === null ? (
        error === undefined ? (
          <p className="parent-muted">{t("parent.loading")}</p>
        ) : null
      ) : (
        <>
          <Row
            label={t("review.label")}
            labelId={labelId}
            hint={t("review.hint")}
            hintLabel={t("review.hintLabel")}
          >
            <Toggle
              labelId={labelId}
              checked={view.allowed}
              onChange={(allowed) => void change(allowed)}
            />
          </Row>
          {since === null ? null : (
            <p className="parent-muted">{t("review.off", { date: since })}</p>
          )}
        </>
      )}
    </section>
  );
}
